import type { Toast } from 'types/common';
import { createWithEqualityFn } from 'zustand/traditional';

const getInitialBrowserPathname = () =>
  typeof globalThis.window === 'undefined'
    ? ''
    : globalThis.window.location.pathname;

export interface NavigationStore {
  browserPathname: string;
  isOffline: boolean;
  isRightPanelOpen: boolean;
  isSelectedAudienceTooltipOpen: boolean;
  isSidebarExpanded: boolean;
  isSidebarOpen: boolean;
  isSourceInfoDialogOpen: boolean;
  pathname: string;
  rightPanelProps: {
    content?: string;
    title?: string;
    isOpen: boolean;
    onToggleCallback: (() => void) | null;
  };
  toast: Toast;
  setIsOffline: (isOffline: boolean) => void;
  setIsSidebarExpanded: (expanded: boolean) => void;
  setIsSidebarOpen: (open: boolean) => void;
  setIsRightPanelOpen: (open: boolean) => void;
  setIsSourceInfoDialogOpen: (open: boolean) => void;
  setIsSelectedAudienceTooltipOpen: (open: boolean) => void;
  setBrowserPathname: (pathname: string) => void;
  setPathname: (pathname: string) => void;
  setRightPanelProps: (props: NavigationStore['rightPanelProps']) => void;
  setToast: (toast: Toast) => void;
  reset: () => void;
}

export const useShellStore = createWithEqualityFn<NavigationStore>(
  (set, get) => ({
    browserPathname: getInitialBrowserPathname(),
    isOffline: false,
    isRightPanelOpen: false,
    isSidebarExpanded: true,
    isSidebarOpen: false,
    isSelectedAudienceTooltipOpen: true,
    isSourceInfoDialogOpen: false,
    pathname: '',
    rightPanelProps: {
      isOpen: false,
      onToggleCallback: null
    },
    toast: { isOpen: false, message: '' },
    setBrowserPathname: (browserPathname) => set({ browserPathname }),
    setPathname: (pathname) => set({ pathname }),
    setIsOffline: (isOffline) => set({ isOffline }),
    setIsSidebarExpanded: (expanded) => set({ isSidebarExpanded: expanded }),
    setIsSidebarOpen: (open) => set({ isSidebarOpen: open }),
    setIsRightPanelOpen: (open) => {
      set({
        isRightPanelOpen: open,
        rightPanelProps: { ...get().rightPanelProps, isOpen: open }
      });
      get().rightPanelProps.onToggleCallback?.();
    },
    setToast: (toast) =>
      set({
        toast: {
          isOpen: Boolean(toast.message),
          ...toast,
          position: toast.position || 'absolute',
          timeout: toast.timeout || 2000
        }
      }),
    setIsSourceInfoDialogOpen: (open) => set({ isSourceInfoDialogOpen: open }),
    setIsSelectedAudienceTooltipOpen: (open) =>
      set({ isSelectedAudienceTooltipOpen: open }),
    setRightPanelProps: (props) =>
      set({ rightPanelProps: props, isRightPanelOpen: props.isOpen }),
    reset: () =>
      set(() => ({
        isSidebarOpen: false,
        isRightPanelOpen: false,
        rightPanelProps: {
          isOpen: false,
          onToggleCallback: null
        }
      }))
  })
);
